import type { MediaAvailability } from './mediaAvailability';
import type { CloudLiveConfig } from './cloudLiveConfig';
import type { LiveRecordingState } from '../live/liveRecordingStates';
import type { UpdateLiveSourceDtoV2 } from './liveSourceDtos';
import type { NdiSourceOrDefault } from './ndiDtos';
import type { PipelineActionResult } from '../live/pipelineActionResult';
import type { LiveSourcePipelineId } from './liveSourceType';
import type { LiveRecordingIds, TsStream } from './liveItemProps';

/**
 * ts segments use a 90Hz timebase.
 */
export const MPEGTS_TIMEBASE = 90000;


/**
 * This describes the information needed to create a new input flow
 * for live.
 */
export interface FlowCreationRequestDto {

  /**
   * Name of the flow.
   */
  name: string;

  /**
   * A CIDR that describes the IP range that is allowed
   * to send content to this flow of the form <IP address>/<bit mask>
   *
   * When testing from home, the IP address is the public IP of
   * your router and the bitmask is the number of bits to 1 in your
   * IP subnet mask (see for instance https://www.pawprint.net/designresources/netmask-converter.php)
   */
  allowedCidr: string;
}

export interface StartStopActionDto {
  action: 'start' | 'stop';
}

/**
 * This describes the information needed to create a new MediaLive channel.
 */
export interface ChannelCreationRequestDto {
  /**
   * Name of the channel.
   */
  name: string;

  /**
   * The ID of the input the use for this channel.
   */
  inputId: string;

  /**
   * The ID of the Mimir item we want to populate with the
   * segments recorded by this channel.
   */
  itemId: string;

  /**
   * The output settings to use for this channel.
   */
  outputSettings?: CloudLiveConfig;
}


/**
 * This describes the information needed to create a new MediaLive input from a flow.
 */
export interface InputCreationRequestDto {
  /**
   * Name of the input.
   */
  name: string;

  /**
   * The arn of the flow to use as input.
   */
  flowArn: string;
}

// Non-scheduled live recordings duration at least 5 minutes long, and at most one day.
export const NON_SCHEDULED_LIVE_MIN_PLANNED_DURATION_IN_MINUTES = 5;
export const NON_SCHEDULED_LIVE_MAX_PLANNED_DURATION_IN_MINUTES = 24 * 60;

export interface LiveRecordingFlowStatus {
  ingestIp: string;
  ingestPort: number;
  ingestUrl: string;
  status: 'STANDBY' | 'ACTIVE' | 'UPDATING' | 'DELETING' | 'STARTING' | 'STOPPING' | 'ERROR' | string;
  errorMessages: string[];
}

export interface LiveRecordingChannelStatus {
  state: 'CREATING' | 'CREATE_FAILED' | 'IDLE' | 'STARTING' | 'RUNNING' | 'RECOVERING' | 'STOPPING' | 'DELETING' | 'DELETED' | 'UPDATING' | 'UPDATE_FAILED' | string;
}

export interface LiveRecordingItemStateDto {
  state: LiveRecordingState;
  highresData?: TsStream;
  webproxyData?: TsStream;
  media?: MediaAvailability;
  mediaDuration?: number;
  liveRecordingIds?: LiveRecordingIds;
  flowStatus?: LiveRecordingFlowStatus;
  channelStatus?: LiveRecordingChannelStatus;
}

export interface StopLiveRecordingRequestDto {
  targetState: Extract<LiveRecordingState, 'resources_deleted'>,

  reason:
    // A user or external system wants to stop the recording.
    // For example a request from our own scheduler client or
    // an external api client.
    | 'external'
    // Used by Kelda when triggering a stop due to not
    // seeing new bytes for some threshold time
    | 'stoppedGrowing';
}

export interface FailLiveRecordingRequestDto {
  targetState: Extract<LiveRecordingState, 'error'>,
  error: string,
}

export type LiveItemStateRequestDto = StopLiveRecordingRequestDto | FailLiveRecordingRequestDto;

export interface ElementalProxyPipelineActionResultDto {
  pipelineActionResult: PipelineActionResult;
  commandType: 'startSrt' | 'stopSrt' | 'startS3' | 'stopS3';
}

export type LiveTriggerEvent = {
  itemId: string,
  tenantId: string,
  operation: 'initialize',
  runLocally: boolean,
};

export type LiveSourceUpdateEvent = {
  tenantId: string,
  operation: 'liveSourceUpdate',
  updateLiveSourceDto: UpdateLiveSourceDtoV2,
};


export type SeriesRecordingParams = {
  durationMs: number;
  ingestFrom: LiveSourcePipelineId;
  routerSource: number | undefined;
  ndiSource: NdiSourceOrDefault,
  outputSettings?: CloudLiveConfig;
  dynamicSource?: string,
};

export interface SeriesCreationFromItem {
  // The item that will serve as the template for the series,
  // by changed from a single recording into a recurring recording.
  // The item must be a scheduled recording item not currently
  // part of a series.
  itemId: string;
}
